<template>
  <div class="auth-page">
    <div class="container py-5">
      <div class="row justify-content-center align-items-center min-vh-100">
        <div class="col-md-6 col-lg-5">
          <div class="auth-card p-4 shadow rounded-4">
          <!-- Logo/Título -->
          <div class="text-center mb-4">
            <img src="@/assets/images/LOGO.svg" alt="Kame Seeds logo" class="img-fluid col-3">
            <h4 class="text-light mt-3">{{ showActivationHint ? 'Activá tu cuenta' : (showResetPassword ? 'Recuperar contraseña' : 'Iniciar Sesión') }}</h4>
          </div>

          <!-- Cuenta no verificada: activar por link -->
          <div v-if="showActivationHint" class="activation-hint">
            <div class="activation-icon mb-4 mx-auto">
              <i class="fas fa-envelope-open-text"></i>
            </div>
            <p class="text-center text-white mb-3">
              Tu cuenta aún no está activa. Revisá tu email
              <strong v-if="verificationEmail"> ({{ verificationEmail }})</strong>
              y hacé clic en el <strong>link de activación</strong>.
            </p>
            <p class="text-center text-muted small mb-4">
              Si no lo encontrás, revisá la carpeta de spam o reenviá el email.
            </p>
            <button
              @click="resendActivationLink"
              class="btn btn-outline-light w-100 py-2 mb-3"
              :disabled="isResending"
            >
              <span v-if="isResending" class="spinner-border spinner-border-sm me-2"></span>
              {{ isResending ? 'Enviando…' : 'Reenviar email de activación' }}
            </button>
            <div class="text-center">
              <button @click="backToLogin" class="btn btn-link text-primary">
                Volver al inicio de sesión
              </button>
            </div>
          </div>

          <!-- Recuperar contraseña -->
          <div v-else-if="showResetPassword" class="reset-password">
            <!-- Paso 1: pedir el código -->
            <template v-if="resetStep === 'request'">
              <div class="activation-icon mb-4 mx-auto">
                <i class="fas fa-key"></i>
              </div>
              <p class="text-center text-white mb-3">
                Ingresá tu <strong>usuario o email</strong> y te enviaremos un
                <strong>código</strong> para restablecer tu contraseña.
              </p>

              <div class="form-floating mb-3">
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': resetV$.identifier.$error }"
                  id="resetIdentifier"
                  v-model="resetForm.identifier"
                  placeholder="Usuario o Email"
                  required
                >
                <label for="resetIdentifier">
                  <i class="fas fa-user text-muted me-2"></i>Usuario o Email
                </label>
                <div class="invalid-feedback" v-if="resetV$.identifier.$error">
                  {{ resetV$.identifier.$errors[0].$message }}
                </div>
              </div>

              <div v-if="resetError" class="alert alert-danger mb-3">
                {{ resetError }}
              </div>

              <button
                @click="submitResetRequest"
                class="btn btn-primary w-100 py-3 mb-3"
                :disabled="isRequestingReset"
              >
                <span v-if="isRequestingReset" class="spinner-border spinner-border-sm me-2"></span>
                {{ isRequestingReset ? 'Enviando…' : 'Enviar código' }}
              </button>

              <div class="text-center">
                <button @click="closeResetPassword" class="btn btn-link text-primary">
                  Volver al inicio de sesión
                </button>
              </div>
            </template>

            <!-- Paso 2: código + nueva contraseña -->
            <template v-else>
              <div class="activation-icon mb-4 mx-auto">
                <i class="fas fa-envelope-open-text"></i>
              </div>
              <p class="text-center text-white mb-3">
                Te enviamos un <strong>código de 6 dígitos</strong> a tu email
                <strong v-if="resetMaskedEmail">({{ resetMaskedEmail }})</strong>.
                Ingresalo junto con tu nueva contraseña.
              </p>
              <p v-if="resetMessage" class="text-center text-muted small mb-3">
                {{ resetMessage }}
              </p>

              <div class="form-floating mb-3 verification-code-input">
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': resetV$.code.$error }"
                  id="resetCode"
                  v-model="resetForm.code"
                  placeholder="Código"
                  maxlength="6"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  required
                >
                <label for="resetCode">
                  <i class="fas fa-hashtag text-muted me-2"></i>Código de verificación
                </label>
                <div class="invalid-feedback" v-if="resetV$.code.$error">
                  {{ resetV$.code.$errors[0].$message }}
                </div>
              </div>

              <div class="form-floating mb-3">
                <input
                  :type="showNewPassword ? 'text' : 'password'"
                  class="form-control"
                  :class="{ 'is-invalid': resetV$.newPassword.$error }"
                  id="resetNewPassword"
                  v-model="resetForm.newPassword"
                  placeholder="Nueva contraseña"
                  required
                >
                <label for="resetNewPassword">
                  <i class="fas fa-lock text-muted me-2"></i>Nueva contraseña
                </label>
                <span
                  class="password-toggle"
                  @click="showNewPassword = !showNewPassword"
                >
                  <i :class="showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </span>
                <div class="invalid-feedback" v-if="resetV$.newPassword.$error">
                  {{ resetV$.newPassword.$errors[0].$message }}
                </div>
              </div>

              <div class="form-floating mb-3">
                <input
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="form-control"
                  :class="{ 'is-invalid': resetV$.confirmPassword.$error }"
                  id="resetConfirmPassword"
                  v-model="resetForm.confirmPassword"
                  placeholder="Repetir contraseña"
                  required
                >
                <label for="resetConfirmPassword">
                  <i class="fas fa-lock text-muted me-2"></i>Repetir contraseña
                </label>
                <span
                  class="password-toggle"
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </span>
                <div class="invalid-feedback" v-if="resetV$.confirmPassword.$error">
                  {{ resetV$.confirmPassword.$errors[0].$message }}
                </div>
              </div>

              <div v-if="resetError" class="alert alert-danger mb-3">
                {{ resetError }}
              </div>

              <button
                @click="submitResetConfirm"
                class="btn btn-primary w-100 py-3 mb-3"
                :disabled="isConfirmingReset"
              >
                <span v-if="isConfirmingReset" class="spinner-border spinner-border-sm me-2"></span>
                {{ isConfirmingReset ? 'Guardando…' : 'Cambiar contraseña' }}
              </button>

              <div class="text-center d-flex flex-column gap-1">
                <button @click="backToResetRequest" class="btn btn-link text-primary">
                  ¿No te llegó el código? Usar otro email / reenviar
                </button>
                <button @click="closeResetPassword" class="btn btn-link text-primary">
                  Volver al inicio de sesión
                </button>
              </div>
            </template>
          </div>

          <!-- Formulario de Login -->
          <form v-else @submit.prevent="handleSubmit" class="needs-validation" novalidate>            <!-- Campo Usuario o Email -->
            <div class="form-floating mb-3">
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': v$.username.$error }"
                id="username"
                v-model="formData.username"
                placeholder="Usuario o Email"
                required
              >
              <label for="username">
                <i class="fas fa-user text-muted me-2"></i>Usuario o Email
              </label>
              <div class="invalid-feedback" v-if="v$.username.$error">
                {{ v$.username.$errors[0].$message }}
              </div>
            </div>

            <!-- Campo Contraseña -->
            <div class="form-floating mb-3">
              <input
                :type="showPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': v$.password.$error }"
                id="password"
                v-model="formData.password"
                placeholder="Contraseña"
                required
              >
              <label for="password">
                <i class="fas fa-lock text-muted me-2"></i>Contraseña
              </label>
              <span 
                class="password-toggle"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </span>
              <div class="invalid-feedback" v-if="v$.password.$error">
                {{ v$.password.$errors[0].$message }}
              </div>
            </div>

            <!-- Mensaje de error -->
            <div v-if="errorMessage" class="alert alert-danger mb-3">
              {{ errorMessage }}
            </div>

            <!-- Botón Submit -->
            <button
              type="submit"
              class="btn btn-primary w-100 mb-3 py-3"
              :disabled="isLoading"
            >
              <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
              {{ isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión' }}
            </button>

            <!-- Link a recuperar contraseña -->
            <div class="text-center mb-3">
              <button type="button" @click="openResetPassword" class="btn btn-link text-primary">
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            <!-- Link a Registro -->
            <div class="text-center">
              <p class="text-muted mb-0">
                ¿No tenés cuenta?
                <router-link to="/registro" class="text-primary text-decoration-none ms-1">Registrarse</router-link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, helpers, sameAs } from '@vuelidate/validators'
import { useRouter, useRoute } from 'vue-router'
import { login, resendVerification, requestPasswordReset, confirmPasswordReset } from '@/services/auth'
import { isIngresoHost, ingresoDefaultRoute, mainSiteUrlForAdminOnIngreso } from '@/utils/host'
import { useToast } from 'vue-toastification'

const router = useRouter()
const route = useRoute()
const toast = useToast()

// Estado del formulario
const formData = reactive({
  username: '',
  password: ''
})

// Estado UI
const showPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')

// Estado de cuenta no activada
const showActivationHint = ref(false)
const verificationEmail = ref('')
const isResending = ref(false)

// Estado de recuperación de contraseña
const showResetPassword = ref(false)
const resetStep = ref('request') // 'request' | 'confirm'
const resetMaskedEmail = ref('')
const resetMessage = ref('')
const resetError = ref('')
const isRequestingReset = ref(false)
const isConfirmingReset = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

const resetForm = reactive({
  identifier: '',
  code: '',
  newPassword: '',
  confirmPassword: ''
})

const resetRules = {
  identifier: {
    required: helpers.withMessage('Ingresá tu usuario o email', required)
  },
  code: {
    required: helpers.withMessage('Ingresá el código de verificación', required),
    minLength: helpers.withMessage('El código tiene 6 dígitos', minLength(6))
  },
  newPassword: {
    required: helpers.withMessage('Ingresá la nueva contraseña', required),
    minLength: helpers.withMessage('La contraseña debe tener al menos 8 caracteres', minLength(8))
  },
  confirmPassword: {
    required: helpers.withMessage('Repetí la nueva contraseña', required),
    sameAs: helpers.withMessage('Las contraseñas no coinciden', sameAs('newPassword'))
  }
}

const resetV$ = useVuelidate(resetRules, resetForm)

// Reglas de validación
const rules = {
  username: { required, minLength: minLength(4) },
  password: { required, minLength: minLength(6) }
}

const v$ = useVuelidate(rules, formData)

// Manejo del submit
const handleSubmit = async () => {
  try {
    errorMessage.value = '' // Limpia errores previos
    const isFormValid = await v$.value.$validate()
    
    if (!isFormValid) return
    
    isLoading.value = true
    
    // Usa la función login del servicio de autenticación
    const response = await login(formData.username, formData.password)
    
    // Redirigir al usuario según sus permisos y la ruta intentada
    let redirectPath = route.query.redirect ||
                        (response.is_admin ? '/panel/productos' : (isIngresoHost() ? ingresoDefaultRoute() : '/panel/cuenta'))

    if (response.is_admin && isIngresoHost()) {
      if (!redirectPath.startsWith('/panel')) {
        redirectPath = '/panel/productos'
      }
      window.location.href = mainSiteUrlForAdminOnIngreso(redirectPath)
      return
    }

    router.push(redirectPath)
    
  } catch (error) {
    console.error('Error en login:', error)
    
    // Cuenta no verificada — activar por link en el email
    const data = error.response?.data
    if (data?.code === 'email_not_verified' || data?.error === 'ACCOUNT_NOT_VERIFIED') {
      showActivationHint.value = true
      verificationEmail.value = data.email || ''
      errorMessage.value = ''
      toast.warning(data.error || data.message || 'Activá tu cuenta desde el link del email.')
      return
    }
    
    // Mostrar el error específico del backend (credenciales incorrectas, cuenta inexistente, etc.)
    errorMessage.value = data?.error || error.message
    if (!data?.error) {
      toast.error('Error al iniciar sesión')
    }
  } finally {
    isLoading.value = false
  }
}

const resendActivationLink = async () => {
  if (!verificationEmail.value) {
    toast.error('No tenemos el email de la cuenta. Intentá registrarte de nuevo.')
    return
  }
  try {
    isResending.value = true
    await resendVerification(verificationEmail.value)
    toast.success('Email de activación reenviado')
  } catch (error) {
    if (error.retryAfterSeconds) {
      toast.warning(error.message)
    } else {
      toast.error(error.response?.data?.error || error.message || 'Error al reenviar el email')
    }
  } finally {
    isResending.value = false
  }
}

const backToLogin = () => {
  showActivationHint.value = false
  verificationEmail.value = ''
  errorMessage.value = ''
}

// ── Recuperación de contraseña ────────────────────────────────

const openResetPassword = () => {
  showResetPassword.value = true
  resetStep.value = 'request'
  resetError.value = ''
  resetMessage.value = ''
  resetMaskedEmail.value = ''
  errorMessage.value = ''
  resetV$.value.$reset()
}

const submitResetRequest = async () => {
  resetError.value = ''
  resetMessage.value = ''
  const isFormValid = await resetV$.value.$validate()
  if (!isFormValid) return

  isRequestingReset.value = true
  try {
    const data = await requestPasswordReset(resetForm.identifier.trim())
    resetMaskedEmail.value = data.masked_email || ''
    resetMessage.value = data.message || 'Te enviamos un código de verificación a tu email.'
    resetStep.value = 'confirm'
    resetV$.value.$reset()
  } catch (error) {
    resetError.value = error?.error || error?.message || 'No se pudo enviar el código. Intentá nuevamente.'
  } finally {
    isRequestingReset.value = false
  }
}

const submitResetConfirm = async () => {
  resetError.value = ''
  const isFormValid = await resetV$.value.$validate()
  if (!isFormValid) return

  isConfirmingReset.value = true
  try {
    const data = await confirmPasswordReset(
      resetForm.identifier.trim(),
      resetForm.code.trim(),
      resetForm.newPassword
    )
    toast.success(data.message || 'Contraseña actualizada correctamente. Ya podés iniciar sesión.')
    // Volver al login con el usuario precargado
    formData.username = resetForm.identifier.trim()
    closeResetPassword()
  } catch (error) {
    resetError.value = error?.error || error?.message || 'No pudimos cambiar la contraseña. Verificá el código e intentá de nuevo.'
  } finally {
    isConfirmingReset.value = false
  }
}

const backToResetRequest = () => {
  resetStep.value = 'request'
  resetError.value = ''
  resetMessage.value = ''
  resetV$.value.$reset()
}

const closeResetPassword = () => {
  showResetPassword.value = false
  resetStep.value = 'request'
  resetError.value = ''
  resetMessage.value = ''
  resetMaskedEmail.value = ''
  showNewPassword.value = false
  showConfirmPassword.value = false
  Object.assign(resetForm, { identifier: '', code: '', newPassword: '', confirmPassword: '' })
  resetV$.value.$reset()
}

// Verificar ruta al montar
onMounted(() => {
  const intendedRoute = localStorage.getItem('intendedRoute')
  if (intendedRoute) {
    localStorage.removeItem('intendedRoute')
  }

  const activated = route.query.activated
  if (activated === 'success') {
    toast.success('¡Cuenta activada! Ya podés iniciar sesión.')
  } else if (activated === 'expired') {
    toast.warning('El link de activación expiró. Pedí uno nuevo.')
  } else if (activated === 'invalid') {
    toast.error('El link de activación no es válido.')
  }

  document.title = 'Iniciar Sesión - Kame Seeds'
})
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  width: 100%;
  background-color: var(--black-color);
  background-image: url('@/assets/images/fondos/fondo-catalogo.svg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  font-family: 'Poppins', sans-serif;
}

.auth-card {
  transition: all 0.3s ease;
  background-color: rgba(27, 27, 27, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.auth-card .form-floating > .form-control {
  font-family: 'Poppins', sans-serif;
  letter-spacing: normal;
  background-color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.12);
  color: var(--black-color);
}

.auth-card .form-floating > .form-control:focus {
  background-color: rgba(255, 255, 255, 0.95);
  border-color: var(--primary-color);
  color: var(--black-color);
  box-shadow: 0 0 0 0.25rem rgba(255, 236, 0, 0.2);
}

.auth-card .form-floating > label {
  font-family: 'Poppins', sans-serif;
  letter-spacing: normal;
  color: rgba(0, 0, 0, 0.6);
}

.auth-card .form-floating > label .fas,
.auth-card .form-floating > label .fab {
  font-family: 'Font Awesome 6 Free' !important;
  font-style: normal;
}

.auth-card .form-floating > label .fab {
  font-family: 'Font Awesome 6 Brands' !important;
}

.auth-card .form-floating > .form-control:focus ~ label,
.auth-card .form-floating > .form-control:not(:placeholder-shown) ~ label {
  color: rgba(0, 0, 0, 0.75);
}

.auth-card .form-floating > label::after {
  background-color: rgba(255, 255, 255, 0.8);
}

.auth-card .form-floating > .form-control:focus ~ label::after,
.auth-card .form-floating > .form-control:not(:placeholder-shown) ~ label::after {
  background-color: rgba(255, 255, 255, 0.95);
}

.auth-card .form-text {
  color: rgba(255, 255, 255, 0.65) !important;
}

.auth-card .form-floating label .text-muted {
  color: rgba(0, 0, 0, 0.45) !important;
}

.form-floating input {
  padding-right: 2.5rem;
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  cursor: pointer;
  color: rgba(0, 0, 0, 0.45);
  z-index: 10;
}

.activation-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(255, 236, 0, 0.12);
  color: var(--primary-color, #ffec00);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
}

.password-toggle:hover {
  color: rgba(0, 0, 0, 0.7);
}
.form-control{
  letter-spacing: .06rem;
}
.btn-primary {
  transition: all 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(13, 110, 253, 0.2);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

@media (max-width: 576px) {
  .auth-card {
    margin: 0 0.5rem;
  }
}

.verification-code-input input {
  font-size: 1.5rem;
  letter-spacing: 0.5rem;
  text-align: center;
}

.btn-link {
  color: var(--primary-color);
  text-decoration: none;
}

.btn-link:hover {
  text-decoration: underline;
}
</style>